const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1';

const MM_BASE = '/material-management';

/* ------------------------------------------------------------------ */
/*  Helper: build URL with query-string params (skips empty values)   */
/* ------------------------------------------------------------------ */
function buildUrl(path, params = {}) {
  const url = new URL(`${API_BASE_URL}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, value);
    }
  });
  return url.toString();
}

async function getJson(url, errLabel) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${errLabel}: ${response.statusText}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.message || errLabel);
  return data;
}

/* ------------------------------------------------------------------ */
/*  Products (dropdown)                                               */
/* ------------------------------------------------------------------ */

/**
 * Product list for the dropdown.
 * @returns {Promise<Array>} [{ id, code, name, theoretical_batch_size }]
 */
export async function fetchProducts(params = {}) {
  try {
    return (await getJson(buildUrl(`${MM_BASE}/products`, params), 'Failed to fetch products')).data;
  } catch (err) {
    console.error('fetchProducts error:', err);
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/*  Live requirement (calculates, stores nothing)                     */
/* ------------------------------------------------------------------ */

/**
 * Live material requirement for a product x N batches.
 * @returns {Promise<Object>} { data: { product, materials, summary } }
 */
export async function fetchRequirement({ product_id, batches } = {}) {
  try {
    const url = buildUrl(`${MM_BASE}/requirement`, { product_id, batches });
    return (await getJson(url, 'Failed to fetch material requirement')).data;
  } catch (err) {
    console.error('fetchRequirement error:', err);
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/*  Plans                                                             */
/* ------------------------------------------------------------------ */

/**
 * Plan list with progress counts.
 * @returns {Promise<Array>} [{ plan_id, plan_name, products, total_batches, completed, running }]
 */
export async function fetchPlans(params = {}) {
  try {
    return (await getJson(buildUrl(`${MM_BASE}/plans`, params), 'Failed to fetch plans')).data;
  } catch (err) {
    console.error('fetchPlans error:', err);
    throw err;
  }
}

/**
 * One plan: completed rows read back frozen, running rows recalculated live.
 * @returns {Promise<Object>} { plan_id, plan_name, summary, products: [...] }
 */
export async function fetchPlanDetail(plan_id) {
  try {
    const url = buildUrl(`${MM_BASE}/plans/${encodeURIComponent(plan_id)}`);
    return (await getJson(url, 'Failed to fetch plan detail')).data;
  } catch (err) {
    console.error('fetchPlanDetail error:', err);
    throw err;
  }
}

/**
 * Create a plan. Writes one Running row per product per batch.
 * @param {Object} body { plan_name, items: [{ product_id, no_of_batches }] }
 */
export async function createPlan(body) {
  try {
    const response = await fetch(`${API_BASE_URL}${MM_BASE}/plans`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.message || 'Failed to create plan');
    return data.data;
  } catch (err) {
    console.error('createPlan error:', err);
    throw err;
  }
}

/**
 * Soft-delete a plan. Frozen completed rows are kept, not destroyed.
 */
export async function deletePlan(plan_id) {
  try {
    const response = await fetch(`${API_BASE_URL}${MM_BASE}/plans/${encodeURIComponent(plan_id)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.message || 'Failed to delete plan');
    return data.data;
  } catch (err) {
    console.error('deletePlan error:', err);
    throw err;
  }
}
